import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setCsrfToken } from "@/lib/api";
import { applyBrandColor } from "@/lib/branding";
import { EMPTY_WORKSPACE } from "@/lib/workspace";
import { MAIN_MODULES } from "@/lib/modules";

const BY_LABEL = Object.fromEntries(MAIN_MODULES.map((m) => [m.label, m.id]));

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({ status: "loading", me: null });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setCsrfToken(data.csrf_token);
      localStorage.setItem("onsuite:uid", data.id || data.user?.id || "");
      const [br, ws] = await Promise.all([
        api.get("/branding").then((r) => r.data).catch(() => null),
        api.get("/workspace").then((r) => r.data).catch(() => EMPTY_WORKSPACE),
      ]);
      applyBrandColor(br?.color);
      setState({ status: "authed", me: { ...data, branding: br, workspace: ws } });
    } catch {
      setCsrfToken(null);
      setState({ status: "anon", me: null });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setCsrfToken(null);
      setState({ status: "anon", me: null });
      window.location.assign("/login");
    }
  }, []);

  const value = useMemo(() => {
    const perms = new Set(state.me?.permissions || []);
    const labels = state.me?.workspace?.labels || {};
    const term = (id, fallback) => labels[id] || fallback;
    const termByLabel = (label) => (typeof label === "string" && labels[BY_LABEL[label]]) || label;
    return { ...state, refresh, logout, can: (p) => perms.has(p), term, termByLabel };
  }, [state, refresh, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
